/**
 * HTML import (fase 3): NoPain's real newsletter
 * (`docs/fase0/eksempel-nyhedsbrev-2026-09-01.md`) is hand-coded HTML
 * with Mailchimp merge tags (`*|FNAME|*` etc.), not something the block
 * editor can reproduce. `docs/decisions.md` "2026-09-23 — Egen
 * HTML-skabelon ved siden af blok-editoren" accepts finished HTML as a
 * second template path, translated to our own merge fields
 * (`./merge-fields.ts`) and put through the same server check
 * (`./server-check.ts`) as a block-rendered template.
 *
 * This module removes Mailchimp's archive-page bar (`./archive-bar.ts`,
 * when the HTML was copied from the web version) and translates the merge
 * tags and the web version's hosted unsubscribe/profile links. It does not
 * sanitise or size-check — call `checkTemplateHtml` from
 * `./server-check.ts` on the result before treating it as safe to save.
 */
import { stripMailchimpArchiveBar } from './archive-bar.js';

export type MailchimpTagOutcome =
  { kind: 'translated' } | { kind: 'unsupported'; reason: string } | { kind: 'unknown' };

type TagRule = { translate: string } | { unsupportedReason: string };

/**
 * Every Mailchimp merge tag this importer recognises by its exact text
 * (between `*|` and `|*`). An `unsupportedReason` entry is a tag we
 * recognise but deliberately don't support: it is still reported as an
 * error, with advice for the marketer instead of "unknown tag". The
 * `FNAME`/`LNAME` tags (with or without a `:fallback`) and the
 * `IF:`/`ELSE:`/`END:IF` conditionals are handled by pattern in
 * {@link ruleFor}.
 */
const MAILCHIMP_TAG_RULES: Record<string, TagRule> = {
  // Default value copied verbatim from the worked example in
  // ../bilag/arkitektur-2026-09-23.md §7.5 — that doc doesn't say
  // whether it's meant literally in English or as a placeholder; revisit
  // with Timo/Gustav before real Danish copy goes out. Mailchimp's own
  // `*|FNAME:friend|*` form picks the fallback itself (see ruleFor).
  FNAME: { translate: '{{ contact.first_name | default: "there" }}' },
  LNAME: { translate: '{{ contact.last_name }}' },
  UNSUB: { translate: '{{ unsubscribe_url }}' },
  ARCHIVE: { translate: '{{ view_url }}' },
  'LIST:ADDRESS': { translate: '{{ company_address }}' },
  'HTML:LIST_ADDRESS_HTML': { translate: '{{ company_address }}' },
  // The preference centre exists since 28/9 (`preferences_url`), so this
  // is no longer the "not in the MVP" case of
  // docs/fase0/eksempel-nyhedsbrev-2026-09-01.md, finding 5.
  UPDATE_PROFILE: { translate: '{{ preferences_url }}' },
  'MC:DATE': {
    unsupportedReason:
      'Send date as a merge field is not supported. Write the date directly in the text.',
  },
  CURRENT_YEAR: {
    unsupportedReason:
      'The current year as a merge field is not supported. Write the year directly in the text.',
  },
  EMAIL: {
    unsupportedReason:
      "The recipient's email address must not be inserted into the mail text itself (PII). Do not use this merge field.",
  },
  FORWARD: {
    unsupportedReason:
      'Forward-to-a-friend links are not supported. Remove the link or replace it with a normal link.',
  },
  'MC:SUBJECT': {
    unsupportedReason:
      'The subject line as a merge field is not supported. Write the text directly or remove it.',
  },
  MC_PREVIEW_TEXT: {
    unsupportedReason:
      'Preview text is set on the campaign (Preheader), not inside the HTML. Remove this tag.',
  },
  REWARDS: {
    unsupportedReason: 'The Mailchimp referral badge does not exist here. Remove this tag.',
  },
};

const CONDITIONAL_REASON =
  'Conditional blocks (*|IF:...|* ... *|END:IF|*) are not supported. Keep only the version you want to send and remove the conditional tags.';

/** Only these characters may appear in a `*|FNAME:fallback|*` fallback — it is placed inside a quoted `default:` literal, so no quotes, backslashes, braces or markup. */
const SAFE_FALLBACK_PATTERN = /^[^|*{}<>"\\\r\n]{1,60}$/;

/** What a single tag (the text between `*|` and `|*`) turns into, or why it can't. `undefined` = not recognised at all. */
function ruleFor(inner: string): TagRule | undefined {
  const exact = MAILCHIMP_TAG_RULES[inner];
  if (exact) return exact;

  const colon = inner.indexOf(':');
  const name = colon === -1 ? inner : inner.slice(0, colon);
  const arg = colon === -1 ? '' : inner.slice(colon + 1);

  if ((name === 'FNAME' || name === 'LNAME') && arg !== '') {
    if (!SAFE_FALLBACK_PATTERN.test(arg)) {
      return {
        unsupportedReason: `The fallback text after ${name}: contains characters that are not allowed. Use plain words only.`,
      };
    }
    const field = name === 'FNAME' ? 'contact.first_name' : 'contact.last_name';
    return { translate: `{{ ${field} | default: "${arg}" }}` };
  }
  if (
    name === 'IF' ||
    name === 'IFNOT' ||
    name === 'ELSEIF' ||
    name === 'ELSE' ||
    inner === 'END:IF'
  ) {
    return { unsupportedReason: CONDITIONAL_REASON };
  }
  return undefined;
}

/** Any `*|TAG|*` — kept loose on purpose so a lower-case or oddly written tag is reported instead of silently shipped. */
const MAILCHIMP_TAG_PATTERN = /\*\|([^|*{}<>"\\\r\n]{1,80}?)\|\*/g;

/**
 * Mailchimp's web version replaces `*|UNSUB|*` and `*|UPDATE_PROFILE|*`
 * with its own hosted pages (`<account>.usNN.list-manage.com/unsubscribe?u=…&id=…&e=[UNIQID]`
 * and `/profile?…`). Measured on NoPain's 20 archived newsletters: every
 * one has exactly one of each, always as an `href`. Left as they are,
 * they unsubscribe from the Mailchimp list, not from us. Only the exact
 * paths are matched (not `/unsubscribe/post` etc.), and only as a whole
 * quoted `href` value (not `data-href`).
 */
const MAILCHIMP_HOSTED_LINK_PATTERN =
  /(\shref\s*=\s*)(["'])\s*https?:\/\/(?:[a-z0-9-]+\.)*list-manage\.com\/(unsubscribe|profile)\/?(?:\?[^"']*)?\s*\2/gi;

const MAILCHIMP_HOSTED_LINK_RULES: Record<string, HtmlImportTranslation> = {
  unsubscribe: { tag: 'list-manage.com/unsubscribe', to: '{{ unsubscribe_url }}', count: 0 },
  profile: { tag: 'list-manage.com/profile', to: '{{ preferences_url }}', count: 0 },
};

export interface HtmlImportError {
  tag: string;
  message: string;
  /** How many times the tag occurs in the input. */
  count: number;
}

export interface HtmlImportTranslation {
  /** The tag as written in Mailchimp, e.g. `*|FNAME|*`. */
  tag: string;
  /** What it became, e.g. `{{ contact.first_name | default: "there" }}`. */
  to: string;
  count: number;
}

export interface HtmlImportResult {
  /** Input with every recognised, supported tag translated to our `{{ ... }}` syntax. Unsupported/unknown tags are left as-is (still visible for review) — never treat this as safe to save when `errors.length > 0`. */
  html: string;
  errors: HtmlImportError[];
  /** Distinct tags translated (inner text, e.g. `FNAME`), in first-seen order — for a human-readable import summary. */
  translatedTags: string[];
  /** Same distinct tags with their result and how often they occur, followed by Mailchimp-hosted unsubscribe/profile links (`tag` is then e.g. `list-manage.com/unsubscribe`). */
  translations: HtmlImportTranslation[];
  /** True when Mailchimp's archive-page bar (Share, Past Issues, Translate) and its scripts were removed. */
  archiveBarRemoved: boolean;
}

/** Removes Mailchimp's archive-page bar, translates Mailchimp merge tags and Mailchimp-hosted unsubscribe/profile links in `html` to our own `{{ ... }}` merge fields, and reports every unsupported or unrecognised tag as a structured error instead of silently dropping it. */
export function importHtmlTemplate(input: string): HtmlImportResult {
  const { html, removed: archiveBarRemoved } = stripMailchimpArchiveBar(input);
  const errorsByTag = new Map<string, HtmlImportError>();
  const translationsByTag = new Map<string, HtmlImportTranslation>();
  const linkTranslations = new Map<string, HtmlImportTranslation>();

  const withOwnLinks = html.replace(
    MAILCHIMP_HOSTED_LINK_PATTERN,
    (_raw, attr: string, quote: string, page: string) => {
      const rule = MAILCHIMP_HOSTED_LINK_RULES[page.toLowerCase()]!;
      const seen = linkTranslations.get(rule.tag);
      if (seen) seen.count += 1;
      else linkTranslations.set(rule.tag, { ...rule, count: 1 });
      return `${attr}${quote}${rule.to}${quote}`;
    },
  );

  const translated = withOwnLinks.replace(MAILCHIMP_TAG_PATTERN, (raw, inner: string) => {
    const rule = ruleFor(inner);

    if (rule && 'translate' in rule) {
      const seen = translationsByTag.get(raw);
      if (seen) seen.count += 1;
      else translationsByTag.set(raw, { tag: raw, to: rule.translate, count: 1 });
      return rule.translate;
    }

    const seen = errorsByTag.get(raw);
    if (seen) {
      seen.count += 1;
    } else {
      errorsByTag.set(raw, {
        tag: raw,
        message:
          rule && 'unsupportedReason' in rule
            ? rule.unsupportedReason
            : `Unknown Mailchimp merge field: ${raw}. Remove it or replace it with plain text.`,
        count: 1,
      });
    }
    return raw;
  });

  const tagTranslations = [...translationsByTag.values()];
  return {
    html: translated,
    errors: [...errorsByTag.values()],
    translatedTags: tagTranslations.map((t) => t.tag.slice(2, -2)),
    translations: [...tagTranslations, ...linkTranslations.values()],
    archiveBarRemoved,
  };
}
